import React, { useState, useEffect } from "react";
import env from "../../../configs/envConfig";
import { Block, Button, ButtonRow, Eyebrow, Heading } from "cai-fusion";

// Sample data structure for tips
const tipGroups = {
    TipsAndTricks: [
        {
            grouping: `${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} Tips and Tricks`,
            heading: "Creating with Care: Understanding Copyright & Plagiarism",
            text: `Imagine ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} as a collaborative partner, brimming with ideas drawn from an expansive array of sources. While its ability to generate content is remarkable, it may occasionally produce material reminiscent of existing works. Take a moment to verify your content's originality. Tools like Copyscape or Grammarly's Plagiarism Checker can help you ensure everything is on the up-and-up. It's all about creating with peace of mind!`
        }
    ],
    ProTips: [
        {
            grouping: "Pro Tip",
            heading: "Crafting the Perfect Question",
            text: "Be Specific and Direct: To get the most accurate answer, phrase your question with as much detail as possible. Instead of asking \"How do I fix my computer?\", try \"How do I troubleshoot a connection issue on a Windows 10 laptop?\"",
        },
        {
            grouping: "Pro Tip",
            heading: "Iterative Inquiry",
            text: "Refine and Repeat. If the first answer isn't quite right, try breaking your question down into smaller, more detailed follow-ups. This can lead to clearer answers and better understanding."
        },
        {
            grouping: "Pro Tip",
            heading: "Focus with Keywords",
            text: "If you know what you want to do, identify the most important words related to your query and use them prominently. For instance, use \"Python list comprehension syntax\" instead of \"How do I shorten this code?\""
        },
        {
            grouping: "Pro Tip",
            heading: "Context Counts",
            text: "Provide Background Information. When asking for help, include any relevant context. Mention if you're working on a specific project, the tools you're using, or any constraints you're under."
        },
        {
            grouping: "Pro Tip",
            heading: "Attach Documents When Needed",
            text: `If your query involves a document, make sure to upload it. ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} can analyze the content and provide more targeted help. Remember to check the data sources drawer if you're having trouble locating your uploaded file.`,
        },
        {
            grouping: "Pro Tip",
            heading: "Data Privacy Practices",
            text: `Keep in mind that your interactions with ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} are private. Your data remains within the network and isn't used to retrain the model, so you can discuss sensitive topics without worry.`
        },
        {
            grouping: "Pro Tip",
            heading: "Feedback for Future Fixes",
            text: "If something doesn't seem right, use the \"Report an Issue\" button. Your input helps identify and fix problems, ensuring a better experience for everyone."
        },
        {
            grouping: "Pro Tip",
            heading: "Stay Curious and Updated",
            text: `As you use ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"}, you'll learn new ways to interact with the AI. Keep an eye on updates and experiment with different approaches to get the most out of your experience.`
        },
        {
            grouping: "Pro Tip",
            heading: "Regenerate for a Refresh!",
            text: `Feeling like the answer missed the mark? No problem! Just click on the \"Regenerate Message\" icon to give ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} another shot at providing the response you're looking for. It's like asking your question again without having to retype it!`
        },
        {
            grouping: "Pro Tip",
            heading: "Quick Edits, Better Answers!",
            text: `Oops, did you realize you didn't ask exactly what you meant? No worries! Just tap the edit icon to tweak your message. Once you've made your changes, hit send, and ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} will spring into action with a new response.`
        }
    ],
    FunFacts: [
        {
            grouping: "Fun Fact",
            heading: `${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} generated most of these tips!`,
            text: "That's right! Our AI has been hard at work crafting helpful hints to make your experience smoother and more efficient."
        }
    ],
    NewInMaestro: [
        {
            grouping: `New in ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"}`,
            heading: `Let ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} interpret your pictures!`,
            text: `You can now upload images just like documents and ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"} will answer questions about them, including business process and technical architecture diagrams.`,
        },
        {
            grouping: `New in ${env.REACT_APP_BRANDING_SYMPHONY_NAME ?? "Maestro"}`,
            heading: "Shared Chat is here!",
            text: "Experience our latest feature - Shared Chat! Users can now share their chat process with others, who can join in a read-only mode and follow the conversation as it unfolds in real time. Click the triple dots above the input box to share your chats."
        }
    ],
};

const PCYA_LOADING_TIPS = {
    ProTips: [
        {
            "grouping": "Pro Tip",
            "heading": "Ask a Full Question",
            "text": "How should I phrase my question to get a useful answer? Type your question as a complete sentence, the way you would ask a knowledgeable colleague, rather than entering keywords like a search engine."
        },
        {
            "grouping": "Pro Tip",
            "heading": "One Topic at a Time",
            "text": "Why does my answer sometimes cover too much ground? Focusing each question on a single regulation, requirement, or process step helps the system return precise, relevant guidance instead of a broad overview."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Try Rephrasing if Needed",
            "text": "What should I do if the answer doesn't seem quite right? Rewording your question with a bit more detail, such as the case stage or service type, often surfaces a more accurate and relevant response."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Name the Policy Area",
            "text": "How do I avoid getting a generic answer about placements? Naming the program and policy area in your question helps the system retrieve the most relevant guidance."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Specify the Action",
            "text": "How do I get guidance on a specific decision point? Framing your question around a concrete action produces a more direct and actionable response."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Say Where You Are in the Process",
            "text": "How does the case stage affect the answer I receive? Indicating whether you are at intake, assessment, ongoing, or closure helps the system surface the procedural guidance that applies to that specific point in the case."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Name the Role the Question Is About",
            "text": "Why should I mention whether the question is about a caseworker or a supervisor? Policies often assign different responsibilities by role, and naming the role focuses the answer on what is actually required of that person."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Include Relevant Timeframes",
            "text": "Does it help to mention a deadline or effective date? Referencing a required timeline, such as \"within 24 hours\" or \"prior to placement,\" helps the system prioritize the most applicable regulatory guidance."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Describe the Decision You Are Trying to Make",
            "text": "How does explaining my goal improve the answer? Telling the system what you are trying to decide, such as whether to open a case or which service applies, helps it focus on the policy guidance most relevant to your next action rather than the topic in general."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Use the Correct Legal and Official Program Term",
            "text": "How does regulatory language improve my results? Using the precise legal and official program terms from your training and policy documents will anchor your question to the right Bulletin text and produce more accurate results."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Use Standard Case Terminology",
            "text": "Does it matter whether I say \"substantiated\" instead of \"confirmed\"? Official case terms such as \"indicated report,\" \"preponderance of evidence,\" or \"dispositional finding\" match the language used in the source documents and produce more precise answers."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Recall Phrases From Training or Documents",
            "text": "Should I use language I remember from a Bulletin or training? If a specific phrase, such as \"least restrictive setting\" or \"concurrent planning,\" appears in your training materials, using it directly is likely to match the source language in the system."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Talk to It Like a Colleague",
            "text": "Why shouldn't I just type keywords the way I would in a search engine? This system understands full sentences and professional context, so a conversational question with background detail will return a far more relevant answer than a stripped-down phrase."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Explain Your Situation Briefly",
            "text": "Does sharing context about my case type actually change the answer? A single sentence of background, such as the service type or case stage, helps the system generate a specific, applicable response rather than a general policy overview."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Ask Follow-Up Questions",
            "text": "Do I have to start over if my first answer needs refinement? You can ask a focused follow-up within the same topic and the system will apply your earlier context, though follow-up questions work best when they are closely related to your original question."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Ask It to Explain Policy Language",
            "text": "What if the answer includes regulatory language I don't fully understand? You can ask the system to explain a term or requirement in plain language, which is something a search engine cannot do since it returns documents rather than interpretations."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Ask It to Confirm Your Understanding",
            "text": "Can I check whether I have interpreted a policy correctly? You can describe your understanding of a requirement and ask the system to confirm or clarify it, something a search engine has no way to do."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Ask About Exceptions to a Rule",
            "text": "How do I find out if a policy has any exceptions that apply to my situation? This system can identify and explain exceptions or special conditions within a regulation, rather than returning only the general rule as a search engine would."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Focus on One Regulation at a Time",
            "text": "How do I get a complete answer instead of a partial one? When a question spans several rules at once, the system must balance competing priorities, so breaking it into separate questions produces a more thorough and reliable response for each."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Separate What the Policy Requires From How to Apply It",
            "text": "Should I ask about policy requirements and implementation in the same question? Asking first what a regulation requires, then following up with how to carry it out, produces cleaner answers than combining both into a single question."
        },
        {
            "grouping": "Pro Tip",
            "heading": "Limit to One Age Group When Relevant",
            "text": "Does a child's age affect the policy guidance I'll receive? Many CYS requirements differ by the child's age, so specifying a range, such as \"for youth ages 14 and older,\" scopes the answer to the applicable regulation."
        }
    ]
}

const LoadingTips = ({ isLoading, onTipAction }) => {
    const [currentTip, setCurrentTip] = useState(null);
    const [isTipVisible, setIsTipVisible] = useState(true);

    const getRandomTip = () => {
        // Select tip group based on environment - temp fix
        const currentTips = env.REACT_APP_THEME === "pcya" ? PCYA_LOADING_TIPS : tipGroups
        
        // Get random group
        const groupNames = Object.keys(currentTips);
        const randomGroupName = groupNames[Math.floor(Math.random() * groupNames.length)];
        const randomGroup = currentTips[randomGroupName];

        // Get random tip from group
        const randomTip = randomGroup[Math.floor(Math.random() * randomGroup.length)];
        return randomTip;
    };

    const changeTip = () => {
        const newTip = getRandomTip();
        setCurrentTip(newTip);
        setIsTipVisible(true);
    };

    useEffect(() => {
        let interval;

        if (isLoading) {
            changeTip(); // Initialize with a random tip
            interval = setInterval(changeTip, 60000); // Change tip every few seconds
        } else {
            setCurrentTip(null); // Clear the tip when loading is done
        }

        return () => {
            clearInterval(interval); // Clear interval when the component is unmounted or isLoading changes
        };
    }, [isLoading]);

    const handleHide = () => {
        setIsTipVisible(false);
        onTipAction("hide", currentTip);
    };

    return (
        <>
            {(currentTip && isTipVisible) &&
                <Block isDashed isCompact>
                    <Eyebrow children={currentTip?.grouping} />
                    <Heading size={5} className="l-mt-xs l-mb-none">{currentTip?.heading}</Heading>
                    <p className="a-muted-text a-subtle-text">{currentTip.text}</p>
                    <ButtonRow alignLeft>
                        <Button size="small" onClick={handleHide} children="Hide" className="a-button--link"/>
                    </ButtonRow>
                </Block>
            }
        </>
    );
};

export default LoadingTips;
